import React from "react";
import { useNavigate, NavLink } from "react-router-dom";
import "./NavBar.scss";
import { FiGrid, FiCamera, FiUser, FiSettings, FiMic } from "react-icons/fi";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButtonMui from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";

import AudioRecorder from "../AudioRecorder/Audio3second";

const NavBar: React.FC = () => {
  const navigate = useNavigate();

  const [anchorElUser, setAnchorElUser] =
    React.useState<null | HTMLElement>(null);

  const [isAudioModalOpen, setIsAudioModalOpen] = React.useState(false);
  const [treatmentId, setTreatmentId] = React.useState("");

  const [isCheckingTreatmentAudio, setIsCheckingTreatmentAudio] =
    React.useState(false);
  const [isTreatmentAudioApproved, setIsTreatmentAudioApproved] =
    React.useState(false);
  const [showExistingAudioWarningModal, setShowExistingAudioWarningModal] =
    React.useState(false);
  const [audioCheckError, setAudioCheckError] = React.useState("");

  const handleCreateConsent = () => {
    navigate("/new-consentBundle");
  };

  const handleOpenAudioModal = () => {
    setIsAudioModalOpen(true);
    setIsTreatmentAudioApproved(false);
    setShowExistingAudioWarningModal(false);
    setAudioCheckError("");
  };

  const handleCloseAudioModal = () => {
    setIsAudioModalOpen(false);
    setShowExistingAudioWarningModal(false);
    setIsTreatmentAudioApproved(false);
    setAudioCheckError("");
  };

  const hostname = window.location.hostname;
  const isDevEnvironment =
    hostname === "localhost" || hostname.includes("dev.medicube");

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const logOut = () => {
    Cookies.set("token", "");
    delete axios.defaults.headers.common.Authorization;
    handleCloseUserMenu();
    window.location.replace("/");
  };

  const userQuery = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const { data } = await axios.get("/user", {
        headers: { Accept: "application/json" },
      });

      const u = Array.isArray(data)
        ? typeof data[1] === "object" && data[1] !== null
          ? data[1]
          : data[0]
        : data;

      return u as any;
    },
  });

  const user = userQuery.data;

  const practiceIdRaw = user?.medicube_practice_id;
  const practiceId = practiceIdRaw != null ? Number(practiceIdRaw) : undefined;

  const getPracticeData = useQuery({
    queryKey: ["practice", practiceId],
    queryFn: async () => {
      const { data } = await axios.get(`/practice/${practiceId}`);
      return data;
    },
    enabled: !!practiceId,
  });

  const practiceName = getPracticeData.data?.name;

  const tooltipTitle =
    practiceName ||
    (getPracticeData.isLoading ? "Loading practice..." : "My Account");

  const avatarSrc = user?.image_url || "/user-avatar.png";
  const fallbackLetter = (user?.first_name?.trim()?.[0] || "U").toUpperCase();

  const normalizedTreatmentId = treatmentId.trim();
  const hasTreatmentId = normalizedTreatmentId.length > 0;

  const handleTreatmentIdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const numericValue = event.target.value.replace(/\D/g, "");

    setTreatmentId(numericValue);
    setIsTreatmentAudioApproved(false);
    setShowExistingAudioWarningModal(false);
    setAudioCheckError("");
  };

  React.useEffect(() => {
    if (!hasTreatmentId) {
      setIsTreatmentAudioApproved(false);
      setShowExistingAudioWarningModal(false);
      setAudioCheckError("");
      return;
    }

    const base = process.env.REACT_APP_BASE_URL_V2;

    if (!base) {
      setAudioCheckError("REACT_APP_BASE_URL_V2 is not set.");
      setIsTreatmentAudioApproved(false);
      return;
    }

    const timer = window.setTimeout(async () => {
      setIsCheckingTreatmentAudio(true);
      setAudioCheckError("");
      setIsTreatmentAudioApproved(false);

      try {
        const { data } = await axios.get(
          `${base}audio-recording/check_treatment_plan_id/${encodeURIComponent(
            normalizedTreatmentId
          )}`
        );

        if (data?.result === false) {
          setShowExistingAudioWarningModal(true);
          return;
        }

        setIsTreatmentAudioApproved(true);
      } catch (error: any) {
        console.error("Error checking treatment audio:", error);

        setAudioCheckError(
          error?.response?.data?.detail ||
            error?.response?.data?.error ||
            error?.message ||
            "Failed to check treatment ID. Please try again."
        );

        setIsTreatmentAudioApproved(false);
      } finally {
        setIsCheckingTreatmentAudio(false);
      }
    }, 700);

    return () => window.clearTimeout(timer);
  }, [hasTreatmentId, normalizedTreatmentId]);

  return (
    <>
      {isDevEnvironment && (
        <div className="mc-env-banner">Development Sandbox Environment</div>
      )}

      <header className="mc-nav-shell">
        <div className="mc-nav">
          <div className="mc-nav__top">
            <div className="mc-nav__brand">
              <img
                className="mc-nav__logo"
                src="/logo.png"
                alt="Medicube"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <nav className="mc-nav__links" aria-label="Main">
              <NavLink
                to="/welcome"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FavoriteBorderIcon className="mc-nav__icon" />
                <span className="mc-nav__label">Welcome</span>
              </NavLink>

              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiGrid className="mc-nav__icon" />
                <span className="mc-nav__label">Dashboard</span>
              </NavLink>

              <NavLink
                to="/video-gallery"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiCamera className="mc-nav__icon" />
                <span className="mc-nav__label">Video Gallery</span>
              </NavLink>

              <NavLink
                to="/templatesPagev2"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiUser className="mc-nav__icon" />
                <span className="mc-nav__label">My Consent Templates</span>
              </NavLink>

              <NavLink
                to="/my-plan"
                className={({ isActive }) =>
                  `mc-nav__link ${isActive ? "is-active" : ""}`
                }
              >
                <FiSettings className="mc-nav__icon" />
                <span className="mc-nav__label">My Plan</span>
              </NavLink>
            </nav>

            <div className="mc-nav__actions">
              <button
                className="mc-btn mc-btn--icon"
                onClick={handleOpenAudioModal}
                type="button"
                aria-label="Audio recording"
              >
                <FiMic />
              </button>

              <button
                className="mc-btn mc-btn--primary"
                onClick={handleCreateConsent}
                type="button"
              >
                Create consent form
              </button>

              <Tooltip title={tooltipTitle}>
                <IconButton
                  className="mc-nav__avatarBtn"
                  onClick={handleOpenUserMenu}
                  aria-controls={anchorElUser ? "user-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={anchorElUser ? "true" : undefined}
                >
                  <Avatar className="mc-nav__avatar" alt="User" src={avatarSrc}>
                    {fallbackLetter}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <Menu
          id="user-menu"
          anchorEl={anchorElUser}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/mobile_uploader");
            }}
          >
            <Typography textAlign="center">Image uploader</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleCloseUserMenu();
              navigate("/tutorials");
            }}
          >
            <Typography textAlign="center">My Tutorials</Typography>
          </MenuItem>

          <MenuItem onClick={logOut}>
            <Typography textAlign="center">Logout</Typography>
          </MenuItem>
        </Menu>
      </header>

      <Dialog
        open={isAudioModalOpen}
        onClose={handleCloseAudioModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            maxHeight: "92vh",
          },
        }}
      >
        <div className="mc-audio-modal__header">
          <div className="mc-audio-modal__headerContent">
            <div>
              <h2>Audio recording</h2>
              <p>Record audio first, then add the Treatment ID before uploading.</p>
            </div>

            <div className="mc-audio-modal__treatmentField">
              <label htmlFor="audio-treatment-id">
                Treatment ID <span>*</span>
              </label>

              <input
                id="audio-treatment-id"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                value={treatmentId}
                onChange={handleTreatmentIdChange}
                placeholder="Add treatment ID"
                disabled={false}
                aria-required="true"
              />

              {!hasTreatmentId && (
                <small>A treatment ID is required before uploading audio.</small>
              )}

              {hasTreatmentId && isCheckingTreatmentAudio && (
                <small style={{ color: "#475569", fontWeight: 600 }}>
                  Checking treatment ID...
                </small>
              )}

              {hasTreatmentId &&
                isTreatmentAudioApproved &&
                !isCheckingTreatmentAudio && (
                  <small style={{ color: "#15803d", fontWeight: 600 }}>
                    Treatment ID checked. Upload is ready.
                  </small>
                )}

              {audioCheckError && (
                <small style={{ color: "#dc2626", fontWeight: 600 }}>
                  {audioCheckError}
                </small>
              )}
            </div>
          </div>

          <IconButtonMui
            onClick={handleCloseAudioModal}
            aria-label="Close audio recorder"
            size="small"
          >
            <CloseIcon />
          </IconButtonMui>
        </div>

        <DialogContent className="mc-audio-modal__content">
          <AudioRecorder
            consentBundleId={normalizedTreatmentId}
            treatmentPlanId={normalizedTreatmentId}
            apiBaseUrl={process.env.REACT_APP_BASE_URL_V2}
            uploadEnabled={hasTreatmentId && isTreatmentAudioApproved}
            uploadDisabledMessage={
              hasTreatmentId
                ? "Treatment ID is being checked. Please wait before uploading."
                : "Add a treatment ID before uploading audio."
            }
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={showExistingAudioWarningModal}
        onClose={() => setShowExistingAudioWarningModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            padding: "8px",
          },
        }}
      >
        <div style={{ padding: "22px" }}>
          <h3
            style={{
              margin: "0 0 14px",
              fontSize: "20px",
              fontWeight: 800,
              color: "#111827",
            }}
          >
            Existing audio found
          </h3>

          <p
            style={{
              fontSize: "17px",
              fontWeight: 700,
              lineHeight: "1.6",
              color: "#111827",
              margin: 0,
            }}
          >
            A recording already exists for this treatment plan.
            <br />
            <br />
            Continuing will replace the old audio, transcript, and patient note.
            <br />
            <br />
            Do you want to continue?
          </p>

          <div
            style={{
              marginTop: "18px",
              padding: "12px",
              borderRadius: "12px",
              background: "#fff7ed",
              color: "#9a3412",
              fontSize: "16px",
              fontWeight: 700,
              lineHeight: "1.5",
            }}
          >
            This action cannot be undone after the new recording is processed.
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginTop: "22px",
            }}
          >
            <button
              type="button"
              className="mc-btn"
              onClick={() => {
                setShowExistingAudioWarningModal(false);
                setIsTreatmentAudioApproved(false);
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              className="mc-btn mc-btn--primary"
              style={{ background: "#dc2626" }}
              onClick={() => {
                setShowExistingAudioWarningModal(false);
                setIsTreatmentAudioApproved(true);
                setAudioCheckError("");
              }}
            >
              Yes, continue
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default NavBar;